import { useEffect, useRef } from "react";
import { useSectionScrollProgress } from "@/hooks/useSharedScroll";

/**
 * WhatIsTherapy — cinematic scroll-driven "curtain → zoom → words" moment.
 *
 * Performance model (v3, buttery-smooth pass):
 *   1) NO JS easing / smoothing. Progress is applied 1:1 to scroll
 *      position on every scroll frame — this gives perfect scroll-visual
 *      sync (any lag caused by easing catch-up creates the "not smooth"
 *      feeling users complain about).
 *   2) Single-flight rAF throttle for scroll (one paint per animation frame).
 *   3) All animated properties are compositor-only:
 *        - transform (scale) — GPU
 *        - opacity           — GPU
 *        - clip-path inset() — composited in modern browsers
 *      NO blur animation, NO mix-blend-mode, NO width/height animation.
 *   4) The wrap has a fixed layout box (final fullscreen size). Zoom is
 *      done via `transform: scale()` only → no layout on any frame.
 *   5) `contain: layout paint` + `isolation: isolate` keep this section's
 *      rendering fully isolated from the rest of the page.
 */

const CAPTION_WORDS = [
  "Therapy",
  "isn\u2019t",
  "about",
  "being",
  "judged.",
  "It\u2019s",
  "about",
  "being",
  "seen.",
];

const HIGHLIGHT_START = 0.74;
const HIGHLIGHT_END = 1.0;
const PER_WORD_STEP =
  (HIGHLIGHT_END - HIGHLIGHT_START) / CAPTION_WORDS.length;
const PER_WORD_WINDOW = PER_WORD_STEP * 1.6;

export default function WhatIsTherapy() {
  const sectionRef = useRef<HTMLElement>(null);
  const viewportWRef = useRef(
    typeof window !== "undefined" ? window.innerWidth : 1440,
  );
  const viewportHRef = useRef(
    typeof window !== "undefined" ? window.innerHeight : 900,
  );

  const imageWrapRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const beamGlowRef = useRef<HTMLDivElement>(null);
  const captionRef = useRef<HTMLParagraphElement>(null);
  const captionBackdropRef = useRef<HTMLDivElement>(null);
  const eyebrowRef = useRef<HTMLSpanElement>(null);
  const wordRefsRef = useRef<(HTMLSpanElement | null)[]>([]);

  const lastPaintedRef = useRef({
    clip: -1,
    scale: -1,
    radius: -1,
    imgOpacity: -1,
    beamOpacity: -1,
    captionOpacity: -1,
    backdropOpacity: -1,
    eyebrowOpacity: -1,
    wordProgress: new Array<number>(CAPTION_WORDS.length).fill(-1),
  });

  const boxSizeRef = useRef<{ endW: number; endH: number; startScale: number }>(
    { endW: 1440, endH: 900, startScale: 0.5 },
  );

  const recomputeBoxSize = () => {
    const vw = viewportWRef.current;
    const vh = viewportHRef.current;
    const startW = Math.min(880, vw * 0.88);
    const endWByWidth = vw;
    const endWByHeight = vh * (16 / 10);
    const endW = Math.min(endWByWidth, endWByHeight);
    const endH = endW * (10 / 16);
    const startScale = startW / endW;
    boxSizeRef.current = { endW, endH, startScale };

    const wrap = imageWrapRef.current;
    if (wrap) {
      wrap.style.width = `${endW.toFixed(1)}px`;
      wrap.style.height = `${endH.toFixed(1)}px`;
    }
    lastPaintedRef.current.scale = -1;
    lastPaintedRef.current.radius = -1;
  };

  const colorForWord = (t: number) => {
    const alpha = 0.25 + (0.95 - 0.25) * t;
    return `rgba(255, 255, 255, ${alpha.toFixed(3)})`;
  };

  const paint = (p: number) => {
    const last = lastPaintedRef.current;
    const { startScale } = boxSizeRef.current;

    // ---------- Act 1 pre-fade (eyebrow) ----------
    const eyebrowOpacity = Math.max(0, Math.min(1, p / 0.08));

    // ---------- Act 2 curtain reveal (clip-path is composited) ----------
    const curtainT = Math.max(0, Math.min(1, (p - 0.1) / 0.28));
    const curtainEased = 1 - Math.pow(1 - curtainT, 3);
    const clip = (1 - curtainEased) * 50;
    // image opacity fades in during the curtain phase (replaces blur)
    const imgOpacity = 0.25 + curtainEased * 0.75;

    // ---------- Act 3 zoom to fullscreen (transform-only) ----------
    const zoomT = Math.max(0, Math.min(1, (p - 0.38) / 0.34));
    const zoomEased = 1 - Math.pow(1 - zoomT, 3);
    const scale = startScale + (1 - startScale) * zoomEased;
    const radius = (16 * (1 - zoomEased)) / scale;

    // ---------- beam glow fade-in ----------
    const beamT = Math.max(0, Math.min(1, (p - 0.35) / 0.28));
    const beamOpacity = (1 - Math.pow(1 - beamT, 2)) * 0.85;

    // ---------- Act 4 caption ----------
    const capT = Math.max(0, Math.min(1, (p - 0.66) / 0.1));
    const captionOpacity = 1 - Math.pow(1 - capT, 2);
    const backdropOpacity = capT;

    // ---------- write to DOM (all compositor-friendly properties) ----------
    const wrap = imageWrapRef.current;
    if (wrap) {
      if (Math.abs(clip - last.clip) > 0.05) {
        wrap.style.clipPath = `inset(${clip.toFixed(2)}% 0 ${clip.toFixed(2)}% 0)`;
        last.clip = clip;
      }
      if (Math.abs(scale - last.scale) > 0.0003) {
        wrap.style.transform = `translate3d(0,0,0) scale(${scale.toFixed(4)})`;
        last.scale = scale;
      }
      if (Math.abs(radius - last.radius) > 0.1) {
        wrap.style.borderRadius = `${radius.toFixed(1)}px`;
        last.radius = radius;
      }
    }

    const image = imageRef.current;
    if (image && Math.abs(imgOpacity - last.imgOpacity) > 0.005) {
      image.style.opacity = imgOpacity.toFixed(3);
      last.imgOpacity = imgOpacity;
    }

    const beam = beamGlowRef.current;
    if (beam && Math.abs(beamOpacity - last.beamOpacity) > 0.005) {
      beam.style.opacity = beamOpacity.toFixed(3);
      last.beamOpacity = beamOpacity;
    }

    const caption = captionRef.current;
    if (caption && Math.abs(captionOpacity - last.captionOpacity) > 0.005) {
      caption.style.opacity = captionOpacity.toFixed(3);
      last.captionOpacity = captionOpacity;
    }

    const backdrop = captionBackdropRef.current;
    if (
      backdrop &&
      Math.abs(backdropOpacity - last.backdropOpacity) > 0.005
    ) {
      backdrop.style.opacity = backdropOpacity.toFixed(3);
      last.backdropOpacity = backdropOpacity;
    }

    const eyebrow = eyebrowRef.current;
    if (eyebrow && Math.abs(eyebrowOpacity - last.eyebrowOpacity) > 0.005) {
      eyebrow.style.opacity = eyebrowOpacity.toFixed(3);
      last.eyebrowOpacity = eyebrowOpacity;
    }

    for (let i = 0; i < CAPTION_WORDS.length; i += 1) {
      const wordStart = HIGHLIGHT_START + i * PER_WORD_STEP;
      const wt = Math.max(0, Math.min(1, (p - wordStart) / PER_WORD_WINDOW));
      const eased = 1 - Math.pow(1 - wt, 3);
      if (Math.abs(eased - last.wordProgress[i]) > 0.005) {
        const el = wordRefsRef.current[i];
        if (el) el.style.color = colorForWord(eased);
        last.wordProgress[i] = eased;
      }
    }
  };

  useEffect(() => {
    const onResize = () => {
      viewportWRef.current = window.innerWidth;
      viewportHRef.current = window.innerHeight;
      recomputeBoxSize();
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSectionScrollProgress(sectionRef, (p, inView) => {
    if (!inView) return;
    paint(p);
  });

  // Initial paint values (SSR-safe estimation) so first frame looks correct
  const initialVW =
    typeof window !== "undefined" ? window.innerWidth : 1440;
  const initialVH =
    typeof window !== "undefined" ? window.innerHeight : 900;
  const initialStartW = Math.min(880, initialVW * 0.88);
  const initialEndW = Math.min(initialVW, initialVH * (16 / 10));
  const initialEndH = initialEndW * (10 / 16);
  const initialStartScale = initialStartW / initialEndW;

  return (
    <section
      ref={sectionRef}
      id="what-is-therapy"
      className="relative w-full bg-background-100"
      style={{
        height: "340vh",
        contain: "layout paint",
        isolation: "isolate",
      }}
    >
      <style>{`
        @keyframes therapyBeamBreathe {
          0%, 100% { opacity: 0.85; }
          50% { opacity: 1; }
        }
        .therapy-beam-breathe {
          animation: therapyBeamBreathe 3s ease-in-out infinite;
        }
      `}</style>

      <div
        className="sticky top-0 w-full h-screen overflow-hidden flex items-center justify-center"
        style={{ contain: "layout paint" }}
      >
        {/* Section eyebrow / meta */}
        <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-28 md:pt-32 pointer-events-none">
          <div className="max-w-[1600px] mx-auto flex items-baseline justify-between">
            <span
              ref={eyebrowRef}
              className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium"
              style={{ opacity: 0 }}
            >
              What therapy is
            </span>
            <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium hidden md:block">
              Chapter 03 · A closer look
            </span>
          </div>
        </div>

        {/* Image container — fixed layout box (fullscreen size), zoom via
            transform:scale only. Fully composited, zero layout on scroll. */}
        <div
          ref={imageWrapRef}
          className="relative overflow-hidden bg-background-200 z-10"
          style={{
            width: `${initialEndW}px`,
            height: `${initialEndH}px`,
            clipPath: "inset(50% 0 50% 0)",
            borderRadius: `${(16 / initialStartScale).toFixed(1)}px`,
            transform: `translate3d(0,0,0) scale(${initialStartScale.toFixed(4)})`,
            transformOrigin: "center center",
            willChange: "transform, clip-path",
            backfaceVisibility: "hidden",
            contain: "layout paint",
          }}
        >
          <img
            ref={imageRef}
            src="https://readdy.ai/api/search-image?query=Dreamy%20pastel%20editorial%20illustration%20of%20two%20feminine%20female%20silhouettes%20sitting%20on%20simple%20slim%20wooden%20round%20stools%20facing%20each%20other%20in%20side%20profile%20with%20clean%20empty%20space%20between%20them%2C%20both%20figures%20female%20with%20shoulder%20length%20wavy%20hair%20and%20soft%20feminine%20posture%20hands%20resting%20on%20lap%2C%20the%20left%20woman%20is%20a%20solid%20deep%20cobalt%20royal%20blue%20silhouette%20with%20a%20subtle%20grain%20and%20noise%20texture%20entirely%20opaque%2C%20the%20right%20woman%20is%20a%20glowing%20luminous%20white%20ivory%20silhouette%20wrapped%20in%20a%20soft%20swirling%20iridescent%20aura%20of%20lavender%20purple%20soft%20pink%20peach%20and%20pale%20cyan%20flames%20like%20a%20gentle%20cosmic%20nebula%20flame%20made%20of%20dreamy%20mist%20tiny%20sparkles%20and%20stardust%2C%20background%20is%20an%20airy%20pastel%20gradient%20blending%20soft%20lavender%20purple%20blush%20pink%20peach%20and%20pale%20sky%20blue%2C%20clean%20pale%20lilac%20floor%20plane%2C%20contemplative%20emotionally%20honest%20atmosphere%2C%20soft%20airbrushed%20gradients%20and%20subtle%20film%20grain%2C%20wide%20negative%20space%2C%20no%20faces%20no%20text%20no%20room%20interior%20no%20furniture%20clutter%20no%20walls&width=1800&height=1125&seq=what-is-therapy-cool-iridescent-women-03&orientation=landscape"
            alt="Two seated women silhouettes facing each other, one deep cobalt blue, the other glowing white wrapped in an iridescent lavender pink and peach aura"
            title="Therapy is being seen — a cool iridescent dreamlike conversation"
            className="absolute inset-0 w-full h-full object-cover object-center select-none pointer-events-none"
            draggable={false}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            style={{
              opacity: 0.25,
              willChange: "opacity",
              transform: "translateZ(0)",
              backfaceVisibility: "hidden",
            }}
          />

          {/* Breathing iridescent centre glow — no filter, no mix-blend-mode.
              Uses only radial-gradient + opacity for the effect. */}
          <div
            ref={beamGlowRef}
            aria-hidden="true"
            className="absolute left-1/2 pointer-events-none therapy-beam-breathe"
            style={{
              top: "10%",
              bottom: "8%",
              width: "42%",
              transform: "translate3d(-50%, 0, 0)",
              background:
                "radial-gradient(50% 60% at 50% 55%, rgba(220, 200, 255, 0.55) 0%, rgba(255, 210, 230, 0.32) 40%, rgba(255, 220, 200, 0.18) 65%, transparent 85%)",
              opacity: 0,
              willChange: "opacity",
              backfaceVisibility: "hidden",
            }}
          ></div>

          {/* Soft vignette — static, no animation */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(120% 90% at 50% 50%, transparent 65%, oklch(var(--foreground-950) / 0.08) 100%)",
            }}
          ></div>
        </div>

        {/* Caption backdrop — dark gradient at bottom, fades in Act 4 */}
        <div
          ref={captionBackdropRef}
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 z-20 pointer-events-none"
          style={{
            height: "38%",
            background:
              "linear-gradient(to top, rgba(20, 15, 40, 0.68) 0%, rgba(20, 15, 40, 0.45) 40%, rgba(20, 15, 40, 0) 100%)",
            opacity: 0,
            willChange: "opacity",
            transform: "translateZ(0)",
          }}
        ></div>

        {/* Caption — word by word ignition */}
        <div className="absolute inset-x-0 bottom-[14%] md:bottom-[16%] z-30 flex items-center justify-center px-6 pointer-events-none">
          <p
            ref={captionRef}
            className="font-heading italic text-center max-w-[900px]"
            style={{
              fontSize: "clamp(1.5rem, 2.6vw, 2.4rem)",
              lineHeight: 1.45,
              fontWeight: 300,
              letterSpacing: "-0.005em",
              opacity: 0,
              fontVariationSettings: "'opsz' 144",
              willChange: "opacity",
              transform: "translateZ(0)",
            }}
          >
            {CAPTION_WORDS.map((word, i) => (
              <span
                key={`${word}-${i}`}
                ref={(el) => {
                  wordRefsRef.current[i] = el;
                }}
                style={{
                  color: "rgba(255, 255, 255, 0.25)",
                }}
              >
                {word}
                {i < CAPTION_WORDS.length - 1 ? "\u00A0" : ""}
              </span>
            ))}
          </p>
        </div>

        {/* Corner meta */}
        <div className="absolute bottom-8 left-6 md:left-10 z-40 flex items-center gap-3">
          <span className="text-[10px] tracking-[0.3em] uppercase font-medium text-white/70">
            Chapter 03
          </span>
          <span className="w-8 h-[1px] bg-white/40"></span>
          <span className="text-[10px] tracking-[0.3em] uppercase font-medium text-white/70">
            A closer look
          </span>
        </div>

        <div className="absolute bottom-8 right-6 md:right-10 z-40 flex items-center gap-3">
          <span className="text-[10px] tracking-[0.3em] uppercase font-medium text-white/70">
            Scroll to reveal
          </span>
          <span className="w-8 h-[1px] bg-white/40"></span>
        </div>
      </div>
    </section>
  );
}